import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  LoaderCircle,
  Pencil,
  Plus,
  Search,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiFetch, readDetailMessage } from "@/lib/api";
import { useVariantImagesMap } from "@/lib/variantImages";
import { ProductImage } from "./ProductImage";

type TaxonomyItem = { id: number; name: string };
type AffectedProduct = {
  id: string;
  name: string;
  name_en: string | null;
  brand: { name: string };
  primary_variant_id: number | null;
};
type Page<T> = { items: T[] };
type TaxonomyKind = "brand" | "category";
type RowState = {
  status: "assigning" | "done" | "leaving";
  targetName: string;
};

export type TaxonomyModalProps = {
  title: string;
  kind: TaxonomyKind;
  onClose: () => void;
  listPath: string;
  createPath: string;
  itemPath: (id: number) => string;
  toPayload: (name: string) => Record<string, string>;
  emptyLabel: string;
  addPlaceholder: string;
};

type ReplacementDropdownProps = {
  options: TaxonomyItem[];
  value?: number;
  placeholder: string;
  disabled?: boolean;
  placement?: "auto" | "top";
  searchPlaceholder: string;
  createLabel: (name: string) => string;
  onCreate: (name: string) => Promise<TaxonomyItem>;
  onChange: (item: TaxonomyItem) => void;
};

function ReplacementDropdown({
  options,
  value,
  placeholder,
  disabled,
  placement = "auto",
  searchPlaceholder,
  createLabel,
  onCreate,
  onChange,
}: ReplacementDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, opensUp: false });
  const selected = options.find(option => option.id === value);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredOptions = options.filter(option =>
    option.name.toLocaleLowerCase().includes(normalizedSearch)
  );
  const hasExactMatch = options.some(
    option => option.name.toLocaleLowerCase() === normalizedSearch
  );

  const positionMenu = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 208;
    const opensUp = placement === "top" || window.innerHeight - rect.bottom < 240;
    setMenuPosition({
      left: Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8),
      top: opensUp ? rect.top - 6 : rect.bottom + 6,
      opensUp,
    });
  };

  useEffect(() => {
    if (!open) return;
    positionMenu();
    setSearch("");
    setCreateError(null);
    window.setTimeout(() => searchRef.current?.focus(), 0);
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, placement]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          positionMenu();
          setOpen(current => !current);
        }}
        className={`flex w-full min-w-0 items-center justify-between gap-1 rounded-full border px-2 py-1.5 text-sm font-medium transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-38 sm:gap-2 sm:px-3 ${
          selected
            ? "border-brand bg-brand/10 text-brand"
            : "border-app-border bg-app-control text-foreground hover:bg-app-control-hover"
        }`}
      >
        <span className="min-w-0 truncate">{selected?.name ?? placeholder}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            left: menuPosition.left,
            top: menuPosition.top,
            transform: menuPosition.opensUp ? "translateY(-100%)" : undefined,
          }}
          className="fixed z-[100] w-52 overflow-hidden rounded-xl bg-app-surface text-foreground shadow-xl ring-1 ring-app-border"
        >
          <div className="border-b border-app-border p-2">
            <div className="flex items-center gap-2 rounded-lg border border-app-border bg-app-control px-2.5 py-2 focus-within:border-brand focus-within:ring-1 focus-within:ring-brand">
              <Search size={14} className="shrink-0 text-app-muted" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setCreateError(null);
                }}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-app-muted sm:text-sm"
              />
            </div>
          </div>
          <ul className="filter-options-scroll max-h-52 overflow-y-auto py-1">
            {filteredOptions.map(option => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-brand/10 cursor-pointer ${
                    value === option.id ? "font-semibold text-brand" : "text-foreground"
                  }`}
                >
                  <span
                    className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-brand ${
                      value === option.id ? "bg-brand text-white" : "bg-transparent"
                    }`}
                  >
                    {value === option.id && <Check size={10} strokeWidth={3} />}
                  </span>
                  <span className="truncate">{option.name}</span>
                </button>
              </li>
            ))}
          </ul>
          {normalizedSearch && !hasExactMatch && (
            <button
              type="button"
              disabled={creating}
              onClick={async () => {
                setCreating(true);
                setCreateError(null);
                try {
                  const created = await onCreate(search.trim());
                  onChange(created);
                  setOpen(false);
                } catch (error) {
                  setCreateError(error instanceof Error ? error.message : String(error));
                } finally {
                  setCreating(false);
                }
              }}
              className="flex w-full items-center gap-2 border-t border-app-border px-3 py-2.5 text-left text-sm font-semibold text-brand transition-colors hover:bg-brand/10 disabled:opacity-50 cursor-pointer"
            >
              {creating ? <LoaderCircle size={15} className="animate-spin" /> : <Plus size={15} />}
              <span className="min-w-0 truncate">{createLabel(search.trim())}</span>
            </button>
          )}
          {createError && <p className="border-t border-app-border px-3 py-2 text-xs text-red-500">{createError}</p>}
        </div>,
        document.body
      )}
    </div>
  );
}

export function TaxonomyModal({
  title,
  kind,
  onClose,
  listPath,
  createPath,
  itemPath,
  toPayload,
  emptyLabel,
  addPlaceholder,
}: TaxonomyModalProps) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<TaxonomyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [listActionError, setListActionError] = useState<string | null>(null);
  const [checkingDeleteId, setCheckingDeleteId] = useState<number | null>(null);

  const [deleteItem, setDeleteItem] = useState<TaxonomyItem | null>(null);
  const [affectedProducts, setAffectedProducts] = useState<AffectedProduct[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [bulkReplacement, setBulkReplacement] = useState<TaxonomyItem>();
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [assignPending, setAssignPending] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const rowTimers = useRef(new Map<string, number[]>());
  const closeTimer = useRef<number | null>(null);

  const variantIds = affectedProducts.flatMap(product =>
    product.primary_variant_id == null ? [] : [product.primary_variant_id]
  );
  const imageUrls = useVariantImagesMap(variantIds);
  const workflowLocked =
    checkingDeleteId != null ||
    assignPending ||
    deletePending ||
    closing ||
    Object.keys(rowStates).length > 0;

  const closeModal = () => {
    if (closing) return;
    setClosing(true);
    closeTimer.current = window.setTimeout(onClose, 240);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !workflowLocked) closeModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [workflowLocked, closing]);

  useEffect(() => {
    setLoading(true);
    apiFetch(`${listPath}?limit=100`)
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((page: Page<TaxonomyItem>) => setItems(page.items))
      .catch(() => setLoadError(t("taxonomy.couldNotLoad")))
      .finally(() => setLoading(false));
  }, [listPath, t]);

  useEffect(() => {
    const timers = rowTimers.current;
    return () => {
      timers.forEach(productTimers => productTimers.forEach(window.clearTimeout));
      if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    };
  }, []);

  const announceChange = (deletedId?: number) => {
    window.dispatchEvent(
      new CustomEvent("taxonomy-changed", { detail: { kind, deletedId } })
    );
  };

  const deleteTaxonomy = async (item: TaxonomyItem) => {
    setDeletePending(true);
    setDeleteError(null);
    setListActionError(null);
    try {
      const res = await apiFetch(itemPath(item.id), { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotDelete")));
      }
      setItems(previous => previous.filter(candidate => candidate.id !== item.id));
      announceChange(item.id);
      if (deleteItem?.id === item.id) closeModal();
      else setDeleteItem(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("taxonomy.couldNotDelete");
      if (deleteItem?.id === item.id) setDeleteError(message);
      else setListActionError(message);
    } finally {
      setDeletePending(false);
      setCheckingDeleteId(null);
    }
  };

  const startEdit = (item: TaxonomyItem) => {
    setEditingId(item.id);
    setEditValue(item.name);
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const saveEdit = async (id: number) => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      setEditError(t("common.required"));
      return;
    }
    setSavingId(id);
    setEditError(null);
    try {
      const res = await apiFetch(itemPath(id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toPayload(trimmed)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotUpdate")));
      }
      const updated: TaxonomyItem = await res.json();
      setItems(previous => previous.map(item => (item.id === id ? updated : item)));
      setEditingId(null);
      announceChange();
    } catch (error) {
      setEditError(error instanceof Error ? error.message : t("taxonomy.couldNotUpdate"));
    } finally {
      setSavingId(null);
    }
  };

  const addItem = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await apiFetch(createPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toPayload(trimmed)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotAdd")));
      }
      const created: TaxonomyItem = await res.json();
      setItems(previous => [...previous, created]);
      setNewName("");
      announceChange();
    } catch (error) {
      setAddError(error instanceof Error ? error.message : t("taxonomy.couldNotAdd"));
    } finally {
      setAdding(false);
    }
  };

  const createReplacement = async (name: string): Promise<TaxonomyItem> => {
    const res = await apiFetch(createPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(toPayload(name)),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotAdd")));
    }
    const created: TaxonomyItem = await res.json();
    setItems(previous => [...previous, created]);
    announceChange();
    return created;
  };

  const openDelete = async (item: TaxonomyItem) => {
    setCheckingDeleteId(item.id);
    setListActionError(null);
    try {
      const res = await apiFetch(`${itemPath(item.id)}/products`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotLoadProducts")));
      }
      const products: AffectedProduct[] = await res.json();
      if (products.length === 0) {
        await deleteTaxonomy(item);
        return;
      }
      setAffectedProducts(products);
      setSelectedProductIds(new Set());
      setBulkReplacement(undefined);
      setRowStates({});
      setDeleteError(null);
      setDeleteItem(item);
    } catch (error) {
      setListActionError(
        error instanceof Error ? error.message : t("taxonomy.couldNotLoadProducts")
      );
    } finally {
      setCheckingDeleteId(null);
    }
  };

  const closeDelete = () => {
    if (workflowLocked) return;
    setDeleteItem(null);
    setDeleteError(null);
  };

  const clearRowTimers = (id: string) => {
    rowTimers.current.get(id)?.forEach(window.clearTimeout);
    rowTimers.current.delete(id);
  };

  const removeFinishedProduct = (id: string) => {
    setAffectedProducts(previous => {
      const next = previous.filter(product => product.id !== id);
      if (next.length === 0 && deleteItem) {
        window.setTimeout(() => void deleteTaxonomy(deleteItem), 0);
      }
      return next;
    });
    setRowStates(previous => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
    clearRowTimers(id);
  };

  const showAssignmentSuccess = (ids: string[], targetName: string) => {
    setSelectedProductIds(previous => {
      const next = new Set(previous);
      ids.forEach(id => next.delete(id));
      return next;
    });
    setRowStates(previous => {
      const next = { ...previous };
      ids.forEach(id => {
        next[id] = { status: "done", targetName };
      });
      return next;
    });
    ids.forEach((id, index) => {
      clearRowTimers(id);
      const leaveTimer = window.setTimeout(() => {
        setRowStates(previous => ({
          ...previous,
          [id]: { ...previous[id]!, status: "leaving" },
        }));
        const removeTimer = window.setTimeout(() => removeFinishedProduct(id), 520);
        rowTimers.current.set(id, [removeTimer]);
      }, 2000 + Math.min(index * 60, 360));
      rowTimers.current.set(id, [leaveTimer]);
    });
  };

  const assignProducts = async (ids: string[], targetId: number, targetName?: string) => {
    if (!ids.length) return;
    const target = items.find(item => item.id === targetId);
    const resolvedTargetName = targetName ?? target?.name;
    if (!resolvedTargetName) return;
    setAssignPending(true);
    setDeleteError(null);
    setRowStates(previous => {
      const next = { ...previous };
      ids.forEach(id => {
        next[id] = { status: "assigning", targetName: resolvedTargetName };
      });
      return next;
    });
    try {
      for (let index = 0; index < ids.length; index += 100) {
        const res = await apiFetch(`/products/${kind}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ids: ids.slice(index, index + 100),
            [`${kind}_id`]: targetId,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotAssign")));
        }
      }
      showAssignmentSuccess(ids, resolvedTargetName);
      announceChange();
    } catch (error) {
      setRowStates(previous => {
        const next = { ...previous };
        ids.forEach(id => delete next[id]);
        return next;
      });
      setDeleteError(error instanceof Error ? error.message : t("taxonomy.couldNotAssign"));
    } finally {
      setAssignPending(false);
    }
  };

  const undoAssignment = async (product: AffectedProduct) => {
    if (!deleteItem) return;
    clearRowTimers(product.id);
    setAssignPending(true);
    setDeleteError(null);
    setRowStates(previous => ({
      ...previous,
      [product.id]: { ...previous[product.id]!, status: "assigning" },
    }));
    try {
      const res = await apiFetch(`/products/${kind}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [product.id], [`${kind}_id`]: deleteItem.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(readDetailMessage(body?.detail, t("taxonomy.couldNotUndo")));
      }
      setRowStates(previous => {
        const next = { ...previous };
        delete next[product.id];
        return next;
      });
      announceChange();
    } catch (error) {
      setRowStates(previous => ({
        ...previous,
        [product.id]: { ...previous[product.id]!, status: "done" },
      }));
      setDeleteError(error instanceof Error ? error.message : t("taxonomy.couldNotUndo"));
    } finally {
      setAssignPending(false);
    }
  };

  const toggleProduct = (id: string) => {
    if (rowStates[id]) return;
    setSelectedProductIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const replacementOptions = items.filter(item => item.id !== deleteItem?.id);
  const selectableProducts = affectedProducts.filter(product => !rowStates[product.id]);
  const allSelected =
    selectableProducts.length > 0 &&
    selectableProducts.every(product => selectedProductIds.has(product.id));

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => {
        if (!workflowLocked) closeModal();
      }}
      data-closing={closing || undefined}
    >
      <div
        className={`taxonomy-modal-panel w-full overflow-hidden rounded-2xl border border-app-border bg-app-surface text-foreground shadow-xl ${
          deleteItem ? "max-w-5xl" : "max-w-md"
        } ${closing ? "taxonomy-modal-panel-exit" : ""}`}
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-center justify-between bg-brand px-5 py-4 text-white">
          <div className="flex min-w-0 items-center gap-2">
            {deleteItem && (
              <button
                type="button"
                onClick={closeDelete}
                disabled={workflowLocked}
                className="rounded-full p-1 hover:bg-white/20 disabled:opacity-50 cursor-pointer"
                aria-label={t("common.back")}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="truncate text-lg font-bold">
              {deleteItem ? t("taxonomy.reassignTitle", { name: deleteItem.name }) : title}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeModal}
            disabled={workflowLocked}
            className="rounded-full p-1 hover:bg-white/20 disabled:opacity-50 cursor-pointer"
            aria-label={t("common.close")}
          >
            <X size={20} />
          </button>
        </div>

        {deleteItem ? (
          <div className="flex max-h-[78vh] flex-col gap-4 overflow-y-auto p-5">
            <p className="text-sm text-app-muted">
              {t("taxonomy.tableHelp", { name: deleteItem.name })}
            </p>

            {replacementOptions.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {t("taxonomy.createReplacement", { type: t(`taxonomy.${kind}`) })}
              </div>
            ) : (
              <div className="rounded-xl border border-app-border">
                <table className="w-full table-fixed border-collapse">
                  <colgroup>
                    <col className="w-[42%] sm:w-20" />
                    <col className="hidden sm:table-column" />
                    {kind === "category" && <col className="hidden w-36 sm:table-column" />}
                    <col className="w-[44%] sm:w-58" />
                    <col className="w-[14%] sm:w-16" />
                  </colgroup>
                  <thead className="border-b border-app-border bg-app-control text-left text-xs font-semibold uppercase tracking-wide text-app-muted">
                    <tr>
                      <th className="px-2 py-3 text-center sm:px-3 sm:text-left">
                        {t("taxonomy.product")}
                      </th>
                      <th className="hidden px-3 py-3 sm:table-cell">{t("product.name")}</th>
                      {kind === "category" && <th className="hidden px-3 py-3 sm:table-cell">{t("common.brand")}</th>}
                      <th className="px-2 py-3 text-center sm:px-3 sm:text-left">{t("taxonomy.moveTo")}</th>
                      <th className="px-3 py-3" aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-app-border">
                    {affectedProducts.map(product => {
                      const rowState = rowStates[product.id];
                      const selected = selectedProductIds.has(product.id);
                      const productName =
                        i18n.language.startsWith("en") && product.name_en
                          ? product.name_en
                          : product.name;
                      const imageUrl =
                        product.primary_variant_id == null
                          ? null
                          : imageUrls[product.primary_variant_id];

                      return (
                        <tr
                          key={product.id}
                          className={`bg-app-surface transition-colors ${
                            rowState?.status === "leaving" ? "taxonomy-row-exit" : ""
                          } ${selected ? "bg-brand/5" : "hover:bg-app-surface-hover"}`}
                        >
                          <td className="px-2 py-2.5 sm:px-3">
                            <div className="taxonomy-row-content flex flex-col items-center gap-1.5 text-center sm:block sm:text-left">
                              <ProductImage
                                src={imageUrl}
                                alt={productName}
                                className="size-10 rounded-lg border border-app-border bg-white sm:size-12"
                              />
                              <span className="line-clamp-2 text-xs font-semibold leading-tight text-foreground sm:hidden">
                                {productName}
                              </span>
                            </div>
                          </td>
                          <td className="hidden px-3 py-2.5 sm:table-cell">
                            <div className="taxonomy-row-content line-clamp-2 text-sm font-semibold text-foreground">
                              {productName}
                            </div>
                          </td>
                          {kind === "category" && (
                            <td className="hidden px-3 py-2.5 text-sm text-app-muted sm:table-cell">
                              <div className="taxonomy-row-content line-clamp-2">{product.brand.name}</div>
                            </td>
                          )}
                          <td className="px-2 py-2.5 text-center sm:px-3 sm:text-left">
                            <div className="taxonomy-row-content flex justify-center sm:block">
                              {rowState ? (
                              rowState.status === "assigning" ? (
                                <span className="inline-flex items-center gap-2 text-sm font-medium text-app-muted">
                                  <LoaderCircle size={17} className="animate-spin" />
                                  {t("taxonomy.assigning")}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => undoAssignment(product)}
                                  disabled={rowState.status === "leaving"}
                                  className="group inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-sm font-semibold text-green-700 disabled:pointer-events-none cursor-pointer"
                                  title={t("taxonomy.undo")}
                                >
                                  <span className="relative size-4">
                                    <Check className="absolute inset-0 transition-opacity group-hover:opacity-0" size={16} strokeWidth={3} />
                                    <Undo2 className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100" size={16} />
                                  </span>
                                  <span className="max-w-30 truncate">{rowState.targetName}</span>
                                </button>
                              )
                              ) : selected ? null : (
                              <ReplacementDropdown
                                options={replacementOptions}
                                placeholder={t("taxonomy.chooseReplacement")}
                                disabled={assignPending}
                                searchPlaceholder={t("taxonomy.searchTaxonomy", { type: t(`taxonomy.${kind}`) })}
                                createLabel={name => t("taxonomy.addNewTaxonomy", { type: t(`taxonomy.${kind}`), name })}
                                onCreate={createReplacement}
                                onChange={target => void assignProducts([product.id], target.id, target.name)}
                              />
                              )}
                            </div>
                          </td>
                          <td className="px-1 py-2.5 text-center sm:px-3">
                            <div className="taxonomy-row-content">
                              {!rowState && (
                              <button
                                type="button"
                                onClick={() => toggleProduct(product.id)}
                                className={`mx-auto flex size-6 items-center justify-center rounded-full border-2 transition-colors cursor-pointer ${
                                  selected
                                    ? "border-brand bg-brand text-white"
                                    : "border-app-border bg-transparent hover:border-brand"
                                }`}
                                aria-label={
                                  selected
                                    ? t("taxonomy.unselectProduct", { name: productName })
                                    : t("taxonomy.selectProduct", { name: productName })
                                }
                              >
                                {selected && <Check size={14} strokeWidth={3} />}
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {deletePending && (
              <div className="flex items-center justify-center gap-2 py-3 text-sm font-medium text-app-muted">
                <LoaderCircle size={18} className="animate-spin" />
                {t("common.deleting")}
              </div>
            )}
            {deleteError && <p className="text-sm text-red-600" role="alert">{deleteError}</p>}

            {replacementOptions.length > 0 && affectedProducts.length > 0 && (
              <div className="sticky bottom-0 flex flex-col gap-3 rounded-xl border border-app-border bg-app-surface/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedProductIds(
                      allSelected
                        ? new Set()
                        : new Set(selectableProducts.map(product => product.id))
                    )
                  }
                  className="inline-flex items-center gap-2 text-sm font-semibold text-foreground cursor-pointer"
                >
                  <span className={`flex size-5 items-center justify-center rounded-full border-2 ${allSelected ? "border-brand bg-brand text-white" : "border-app-border bg-transparent"}`}>
                    {allSelected && <Check size={12} strokeWidth={3} />}
                  </span>
                  {allSelected ? t("taxonomy.clearAll") : t("taxonomy.selectAll")}
                </button>
                <span className="text-sm text-app-muted sm:ml-auto">
                  {t("taxonomy.selectedProducts", { count: selectedProductIds.size })}
                </span>
                <ReplacementDropdown
                  options={replacementOptions}
                  value={bulkReplacement?.id}
                  placeholder={t("taxonomy.chooseReplacement")}
                  disabled={assignPending || selectedProductIds.size === 0}
                  placement="top"
                  searchPlaceholder={t("taxonomy.searchTaxonomy", { type: t(`taxonomy.${kind}`) })}
                  createLabel={name => t("taxonomy.addNewTaxonomy", { type: t(`taxonomy.${kind}`), name })}
                  onCreate={createReplacement}
                  onChange={setBulkReplacement}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (bulkReplacement) {
                      void assignProducts(
                        [...selectedProductIds],
                        bulkReplacement.id,
                        bulkReplacement.name
                      );
                    }
                  }}
                  disabled={!bulkReplacement || selectedProductIds.size === 0 || assignPending}
                  className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                >
                  {allSelected
                    ? t("taxonomy.assignAll")
                    : t("taxonomy.assignSelectedShort", { count: selectedProductIds.size })}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto p-5">
            {loading && <p className="text-sm text-app-muted">{t("taxonomy.loading")}</p>}
            {loadError && <p className="text-sm text-red-500">{loadError}</p>}
            {listActionError && <p className="text-sm text-red-500">{listActionError}</p>}
            {!loading && !loadError && items.length === 0 && (
              <p className="text-sm text-app-muted">{emptyLabel}</p>
            )}
            {!loading && items.map(item => (
              <div key={item.id} className="flex flex-col gap-1">
                <div className="flex items-center gap-2 rounded-lg border border-app-border px-3 py-2">
                  {editingId === item.id ? (
                    <>
                      <input
                        autoFocus
                        type="text"
                        value={editValue}
                        onChange={event => setEditValue(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === "Enter") saveEdit(item.id);
                          if (event.key === "Escape") cancelEdit();
                        }}
                        className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                      />
                      <button type="button" onClick={() => saveEdit(item.id)} disabled={savingId === item.id} className="rounded-lg p-1.5 text-brand hover:bg-brand/10 disabled:opacity-50 cursor-pointer" aria-label={t("common.save")}><Check size={16} /></button>
                      <button type="button" onClick={cancelEdit} disabled={savingId === item.id} className="rounded-lg p-1.5 text-app-muted hover:bg-app-surface-hover disabled:opacity-50 cursor-pointer" aria-label={t("common.cancel")}><X size={16} /></button>
                    </>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{item.name}</span>
                      <button type="button" onClick={() => startEdit(item)} className="rounded-lg p-1.5 text-app-muted hover:bg-brand/10 hover:text-brand cursor-pointer" aria-label={t("common.edit")}><Pencil size={16} /></button>
                      <button
                        type="button"
                        onClick={() => openDelete(item)}
                        disabled={checkingDeleteId != null || deletePending}
                        className="rounded-lg p-1.5 text-app-muted hover:bg-red-500/10 hover:text-red-500 disabled:opacity-50 cursor-pointer"
                        aria-label={t("taxonomy.deleteNamed", { name: item.name })}
                      >
                        {checkingDeleteId === item.id ? <LoaderCircle size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    </>
                  )}
                </div>
                {editingId === item.id && editError && <span className="px-1 text-xs text-red-500">{editError}</span>}
              </div>
            ))}
            <div className="flex flex-col gap-1 rounded-lg border border-dashed border-app-border px-3 py-2">
              <div className="flex items-center gap-2">
                <input type="text" value={newName} onChange={event => setNewName(event.target.value)} onKeyDown={event => { if (event.key === "Enter") addItem(); }} placeholder={addPlaceholder} className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-app-muted" />
                <button type="button" onClick={addItem} disabled={adding || !newName.trim()} className="rounded-lg bg-brand p-1.5 text-white hover:bg-brand/90 disabled:opacity-50 cursor-pointer" aria-label={addPlaceholder}><Plus size={16} /></button>
              </div>
              {addError && <span className="px-1 text-xs text-red-500">{addError}</span>}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
